import React, { useContext, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Heading } from '../../ui/Heading'
import { Form } from './form'
import { Button } from '../../ui/Button'
import styled from 'styled-components'
import { useMultiStepState } from '../../../hooks/useMultiStepState'
import { AdminLoginInfo } from '../../../types/AdminLoginInfo'
import { API, PATH } from '../../../const'
import { useHistory } from 'react-router-dom'
import { MessageModal } from '../../ui/Modal'
import { AdminAuthContext } from '../../../auth/AdminAuthProvider'
import { getStepLabel } from './presenter'
import { PartialLoader } from '../../ui/Loader'

const Component: React.FC = () => {
  const formState = useMultiStepState()
  const history = useHistory()
  const { bearerToken } = useContext(AdminAuthContext)
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false)
  const [isComplete, setIsComplete] = useState(true)

  const defaultValues: Omit<AdminLoginInfo['admin'], 'adminId'> = {
    name: '',
    role: 'confirmer', // 主に追加するとしたら確認者権限かな
    email: '',
  }
  const methods = useForm<AdminLoginInfo['admin']>({
    mode: 'onBlur',
    defaultValues,
  })

  const [createdId, setCreatedId] = useState('')
  const handleModalClose = (): void => {
    setIsCompleteModalOpen(false)
    if (isComplete) {
      history.push(`${PATH.ADMIN_ADMIN_USERS}/${createdId}`)
    }
  }

  const handleSubmit = async (): Promise<void> => {
    await methods.handleSubmit(
      async (data: Omit<AdminLoginInfo, 'adminId'>) => {
        const requestBody = data
        const res = await fetch(API.ADMIN_ADMIN_USERS, {
          headers: { Authorization: 'Bearer ' + bearerToken },
          method: 'post',
          mode: 'cors',
          body: JSON.stringify(requestBody),
        })
        if (res.status >= 400 || res.body === null) {
          setIsComplete(false)
          return
        }
        setIsComplete(true)
        const body = await res.json()
        setCreatedId(body.adminId)
        methods.reset()
      },
    )()
  }

  const [isLoading, setIsLoading] = useState(false)
  const handleClickNext = async () => {
    if (formState.currentStep === 'input') {
      // 入力から確認
      document?.querySelector('main')?.scrollTo(0, 0)
      const isValid = await methods.trigger()
      isValid && formState.nextStep()
    } else {
      // 確認から完了
      setIsLoading(true)
      await handleSubmit()
      setIsCompleteModalOpen(true)
      setIsLoading(false)
    }
  }

  const handleClickBack = async () => {
    if (formState.currentStep === 'input') {
      // 入力から一覧
      history.push(PATH.ADMIN_ADMIN_USERS)
    } else {
      // 確認から入力
      formState.prevStep()
    }
  }

  const getNextButtonLabel = () => {
    if (isLoading) {
      return <PartialLoader />
    }
    return formState.currentStep === 'input' ? '確認' : '保存'
  }

  return (
    <FormProvider {...methods}>
      <Heading type="screenTitle" tag="h1">
        管理者情報 新規作成
        {getStepLabel(formState.currentStep)}
      </Heading>
      <Form readonly={formState.currentStep === 'confirm'} />
      <ButtonWrapper>
        <Button onClick={handleClickNext} disabled={isLoading}>
          {getNextButtonLabel()}
        </Button>
      </ButtonWrapper>
      <ButtonWrapper>
        <BackButton onClick={handleClickBack} disabled={isLoading}>
          戻る
        </BackButton>
      </ButtonWrapper>
      <MessageModal
        isOpen={isCompleteModalOpen}
        title={isComplete ? '登録しました' : '登録に失敗しました'}
        description={
          <ModalMessage>
            {isComplete ? undefined : (
              <p>しばらく経ってから再度お試しください。</p>
            )}
          </ModalMessage>
        }
        closeText="OK"
        onClickClose={handleModalClose}
        onClickOverlay={handleModalClose}
        onPressEscape={handleModalClose}
        id="dialog-message"
      />
    </FormProvider>
  )
}

const ButtonWrapper = styled.div`
  margin-top: 40px;
  width: 800px;
  text-align: center;
`
const BackButton = styled(Button)`
  border: 1px solid #439a89;
  background-color: #fff;
  color: #000;
  font-weight: normal;
`
const ModalMessage = styled.div`
  text-align: center;
`

export default Component
